import { useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { setQuery, executeSearch, clearSearch, toggleModeTag, clearModeTags } from '@/features/search/searchSlice';
import SearchBar from '@/components/ui/SearchBar/SearchBar';
import styles from './MobileSearchBar.module.css';

/**
 * Rendered only on mobile (≤767px via CSS) — placed above the Intelligence tabs
 * in each page so it sits on the page background with no extra chrome.
 */
export default function MobileSearchBar() {
  const dispatch = useAppDispatch();
  const searchQuery = useAppSelector((s) => s.search.query);
  const availableTags = useAppSelector((s) => s.search.availableTags);
  const activeModeTagIds = useAppSelector((s) => s.search.activeModeTagIds);

  const [inputFocused, setInputFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    dispatch(setQuery(value));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length === 0) { dispatch(clearSearch()); return; }
    if (value.length >= 2) {
      debounceRef.current = setTimeout(() => {
        dispatch(executeSearch({ query: value, limit: 15 }));
      }, 500);
    }
  };

  const handleModeTagToggle = (tagId: string) => {
    dispatch(toggleModeTag(tagId));
    if (searchQuery.length >= 2) dispatch(executeSearch({ query: searchQuery, limit: 15 }));
  };

  return (
    <div className={styles.wrap}>
      {/* Search input */}
      <div
        className={styles.inputRow}
        onFocus={() => setInputFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setInputFocused(false);
          }
        }}
      >
        <SearchBar
          placeholder="Search videos, projects, people..."
          value={searchQuery}
          onChange={handleSearchChange}
          fullWidth
        />
      </div>

      {/* Mode suggestion chips — inline below search bar, pushes content down */}
      {inputFocused && searchQuery.length >= 1 && availableTags.length > 0 && (
        <div className={styles.modeDropdown} role="group" aria-label="Search mode suggestions">
          <span className={styles.modeDropdownLabel}>Search by</span>
          {availableTags.map((tag) => (
            <button
              key={tag.id}
              className={`${styles.modeChip} ${activeModeTagIds.includes(tag.id) ? styles.modeChipActive : ''}`}
              onMouseDown={(e) => { e.preventDefault(); handleModeTagToggle(tag.id); }}
              aria-pressed={activeModeTagIds.includes(tag.id)}
              title={tag.description}
            >
              {tag.label}
            </button>
          ))}
        </div>
      )}

      {/* Active filter chips row */}
      {activeModeTagIds.length > 0 && (
        <div className={styles.activeChipsRow}>
          {activeModeTagIds.map((id) => {
            const tag = availableTags.find((t) => t.id === id);
            if (!tag) return null;
            return (
              <button
                key={id}
                className={styles.activeChip}
                onMouseDown={(e) => { e.preventDefault(); handleModeTagToggle(id); }}
                aria-label={`Remove ${tag.label} filter`}
                aria-pressed
              >
                {tag.label}
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M1 1l10 10M11 1L1 11" />
                </svg>
              </button>
            );
          })}
          <button
            className={styles.clearBtn}
            onMouseDown={(e) => {
              e.preventDefault();
              dispatch(clearModeTags());
              if (searchQuery.length >= 2) dispatch(executeSearch({ query: searchQuery, limit: 15 }));
            }}
          >Clear</button>
        </div>
      )}
    </div>
  );
}
